import { object, string, number, boolean, mixed } from 'yup'
import {
  IFile,
  IShipperApplication,
  SHIPPER_TYPE,
  RFP_TYPE,
} from 'apps/marketplace/pages/instant-rfp/types'

export interface INormFileSchema {
  normFileTemplate: IFile
  shipperApplication?: IShipperApplication
  newShipperName: string
  googleSheetsUrl: string
  salesforceRfpId: string
  email: string
  storeInDatabase: boolean
  generateMatchingCarriers: boolean
  auditWorkspace: boolean
  shipperType: SHIPPER_TYPE
  requestForProposalType: RFP_TYPE
  pricingWorkspace: boolean
}

export const NormFileSchema = object<INormFileSchema>({
  normFileTemplate: object().shape({
    file: string().required('Must upload a file'),
    fileFileName: string().required('Must upload a file'),
    uuid: string().required('Must upload a file'),
  }),
  shipperApplication: object<IShipperApplication>().when('shipperType', {
    is: SHIPPER_TYPE.existing,
    then: object().shape({
      id: number().required('Must enter a shipper').typeError('Please enter a number'),
      name: string(),
    }),
    otherwise: object().shape({
      id: number().typeError('Shipper ID must be a number'),
      name: string(),
    }),
  }),
  newShipperName: string().when('shipperType', {
    is: SHIPPER_TYPE.new,
    then: string().required('Must enter a shipper'),
  }),
  googleSheetsUrl: string().required('Must enter a URL'),
  salesforceRfpId: string().required('Must enter a Salesforce ID'),
  email: string().required('Must enter an email address'),
  storeInDatabase: boolean(),
  generateMatchingCarriers: boolean(),
  auditWorkspace: boolean(),
  pricingWorkspace: boolean(),
  shipperType: mixed<SHIPPER_TYPE>().oneOf(Object.values(SHIPPER_TYPE)),
  requestForProposalType: mixed<RFP_TYPE>().oneOf(Object.values(RFP_TYPE)),
})

export interface INormFileInputSchema {
  laneId: string
  altLaneId: string
  startDate: string
  endDate: string
  pricingDurationMnths: number | null
  volumeRelevant: number | null
  pickupCity: string
  pickupState: string
  pickupZip: string
  pickupZoneType: string
  pickupZone: string
  pickupIsoCountryCode: string
  deliveryCity: string
  deliveryState: string
  deliveryZip: string
  deliveryZoneType: string
  deliveryZone: string
  deliveryIsoCountryCode: string
  equipMapped: string
  shipperMiles: number | null
  customerVendorName: string
  pickupDropTrailer: string
  deliveryDropTrailer: string
  extraStops: number | null
  comments: string
}

export const NormFileInputSchema = object<INormFileInputSchema>({
  laneId: string().required('Must enter a lane ID'),
  altLaneId: string(),
  startDate: string(),
  endDate: string(),
  pricingDurationMnths: number()
    .typeError('pricing_duration_mnths must be a number')
    .transform((val, original) => (original === '' ? null : val))
    .nullable(),
  volumeRelevant: number()
    .typeError('volume_relevant must be a number')
    .transform((val, original) => (original === '' ? null : val))
    .nullable(),
  pickupCity: string(),
  pickupState: string(),
  pickupZip: string(),
  pickupZoneType: string(),
  pickupZone: string(),
  pickupIsoCountryCode: string(),
  deliveryCity: string(),
  deliveryState: string(),
  deliveryZip: string(),
  deliveryZoneType: string(),
  deliveryZone: string(),
  deliveryIsoCountryCode: string(),
  equipMapped: string(),
  shipperMiles: number()
    .typeError('shipper_miles must be a number')
    .transform((val, original) => (original === '' ? null : val))
    .nullable(),
  customerVendorName: string(),
  pickupDropTrailer: string(),
  deliveryDropTrailer: string(),
  extraStops: number()
    .typeError('extra_stops must be a number')
    .transform((val, original) => (original === '' ? null : val))
    .nullable(),
  comments: string(),
})
